/**
 * RACEIT cart: free-shipping bar motion + one-tap add for cart picks.
 *
 * The bar (snippets/free-shipping-notice.liquid) and the cross-sell
 * (snippets/raceit-cart-cross-sell.liquid) are rendered by Liquid and
 * re-rendered by the theme's own section rendering whenever the cart changes
 * (cart-items.js on quantity changes, CartDrawer.renderContents after an add).
 * This file only:
 *   - animates the bar from the previous total to the new one, sheds purple
 *     sparks from its tip (more, and faster, the fuller it is) and bursts them
 *     once when the van arrives at free shipping;
 *   - adds a pick (the bar's gap closer or a cross-sell card) to the cart in
 *     one tap and lets the theme re-render.
 */

/* global theme, trapFocus */

(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SPARK_COLORS = ['#6d3bcc', '#7c3aed', '#8b5cf6', '#4e2a93', '#a78bfa'];
  // The burst plays on the lavender "reached" panel, so only the deeper purples.
  const BURST_COLORS = ['#6d3bcc', '#7c3aed', '#5b21b6', '#4e2a93'];
  const MAX_SPARKS = 28;

  /**
   * Says what happened for screen readers. The region sits outside the
   * re-rendered markup so it survives every cart refresh.
   * @param {string} message - Text to announce.
   */
  const announce = (message) => {
    if (!message) return;
    let region = document.getElementById('rxs-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'rxs-live-region';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 60);
  };

  if (!customElements.get('raceit-free-shipping')) {
    // Each cart change replaces the element, so the last value lives out here.
    let lastProgress = null;

    class RaceitFreeShipping extends HTMLElement {
      connectedCallback() {
        const next = Math.min(1, Math.max(0, parseFloat(this.dataset.progress) || 0));
        const prev = lastProgress;
        lastProgress = next;
        this.progress = next;

        if (prev !== null && prev !== next) {
          const msg = this.querySelector('.rfs__msg');
          if (msg) announce(msg.textContent.trim().replace(/\s+/g, ' '));
        }

        if (reducedMotion.matches) return;

        if (prev !== null && prev !== next) {
          this.style.setProperty('--rfs-p', prev);
          this.getBoundingClientRect(); // commit the start position before animating
          this.classList.add('is-animating');
          requestAnimationFrame(() => this.style.setProperty('--rfs-p', next));
          if (prev < 1 && next >= 1) {
            this.classList.add('is-arriving');
            this.burstTimer = setTimeout(() => this.burst(), 650);
          }
        }

        this.startSparks();
      }

      disconnectedCallback() {
        clearTimeout(this.sparkTimer);
        clearTimeout(this.burstTimer);
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }

      /**
       * Sheds sparks while the bar is on screen and short of free shipping.
       * Below 15 % the bar stays calm; from there the rate climbs with progress.
       */
      startSparks() {
        if (this.progress < 0.15 || this.progress >= 1 || !('animate' in Element.prototype)) return;
        this.layer = this.querySelector('.rfs__sparks');
        if (!this.layer) return;

        this.visibilityHandler = this.visibilityHandler || (() => this.schedule(0));
        document.addEventListener('visibilitychange', this.visibilityHandler);
        this.observer = new IntersectionObserver((entries) => {
          this.onScreen = entries[0].isIntersecting;
          this.schedule(250);
        });
        this.observer.observe(this);
      }

      /**
       * Queues the next spark, if the bar can be seen.
       * @param {number} delay - Milliseconds until the next spark.
       */
      schedule(delay) {
        clearTimeout(this.sparkTimer);
        if (!this.onScreen || document.hidden || !this.isConnected) return;
        this.sparkTimer = setTimeout(() => {
          const p = this.progress;
          this.emit(p > 0.9 ? 3 : p > 0.7 ? 2 : 1);
          // 0.15 → one spark every ~1.1 s; 0.95 → one every ~0.25 s
          const interval = 1250 - 1050 * p + Math.random() * 180;
          this.schedule(interval);
        }, delay);
      }

      /**
       * Sparks from the leading edge of the purple fill, drifting back and up.
       * @param {number} count - Sparks to emit.
       */
      emit(count) {
        const fill = this.querySelector('.rfs__fill');
        if (!fill || this.layer.childElementCount > MAX_SPARKS) return;
        const host = this.getBoundingClientRect();
        const rect = fill.getBoundingClientRect();
        const rtl = getComputedStyle(this).direction === 'rtl';
        const x = (rtl ? rect.left : rect.right) - host.left;
        const y = rect.top + rect.height / 2 - host.top;
        const back = rtl ? 1 : -1;

        for (let i = 0; i < count; i += 1) {
          const dx = back * (10 + Math.random() * 36);
          const dy = (Math.random() < 0.78 ? -1 : 0.6) * (8 + Math.random() * 26);
          this.spark(x, y, dx, dy, 4 + Math.random() * 4, 750 + Math.random() * 600);
        }
      }

      /**
       * One burst from the van when the cart reaches free shipping.
       */
      burst() {
        const van = this.querySelector('.rfs__van');
        if (!van || !this.isConnected || !('animate' in Element.prototype)) return;
        this.layer = this.layer || this.querySelector('.rfs__sparks');
        const host = this.getBoundingClientRect();
        const rect = van.getBoundingClientRect();
        const x = rect.left + rect.width / 2 - host.left;
        const y = rect.top + rect.height / 2 - host.top;
        for (let i = 0; i < 26; i += 1) {
          const angle = (Math.PI * 2 * i) / 26 + Math.random() * 0.3;
          const dist = 24 + Math.random() * 34;
          this.spark(x, y, Math.cos(angle) * dist, Math.sin(angle) * dist, 4 + Math.random() * 5, 800 + Math.random() * 600, BURST_COLORS);
        }
      }

      /**
       * Draws one spark and removes it when it fades.
       */
      spark(x, y, dx, dy, size, duration, colors = SPARK_COLORS) {
        const s = document.createElement('span');
        s.className = 'rfs__spark';
        s.style.cssText = `left:${x}px;top:${y}px;width:${size}px;height:${size}px;background:${colors[Math.floor(Math.random() * colors.length)]}`;
        this.layer.appendChild(s);
        // Hold the colour for most of the flight, then fade, so each spark reads.
        const anim = s.animate([
          { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
          { transform: `translate(calc(-50% + ${dx * 0.7}px), calc(-50% + ${dy * 0.7}px)) scale(0.8)`, opacity: 0.85, offset: 0.6 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.4)`, opacity: 0 }
        ], { duration, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' });
        anim.onfinish = () => s.remove();
        anim.oncancel = () => s.remove();
      }
    }

    customElements.define('raceit-free-shipping', RaceitFreeShipping);
  }

  if (!customElements.get('raceit-cart-cross-sell')) {
    // Styling hook only; the behaviour below is delegated from the document so
    // the bar's gap closer and the cross-sell cards share it.
    customElements.define('raceit-cart-cross-sell', class extends HTMLElement {});
  }

  if (window.raceitCartPicksBound) return;
  window.raceitCartPicksBound = true;

  /**
   * Moves focus to the line that was just added, since the button is gone.
   * @param {HTMLElement} cartDrawer - The cart drawer.
   * @param {number} variantId - Added variant.
   */
  const focusLine = (cartDrawer, variantId) => {
    const line = cartDrawer.querySelector(`.cart-item[data-variant-id="${variantId}"]`);
    const target = line?.querySelector('.js-item-name') || cartDrawer.querySelector('.drawer__close-btn');
    if (!target) return;
    if (typeof trapFocus === 'function') {
      trapFocus(cartDrawer, target);
    } else {
      target.focus();
    }
  };

  // Keeps the price (and any compare-at price) in step with the chosen variant.
  document.addEventListener('change', (evt) => {
    const select = evt.target.closest?.('.js-rxs-variant');
    if (!select) return;
    // The cart page renders these inside form#cart; this is not a cart edit.
    evt.stopPropagation();

    const option = select.selectedOptions[0];
    const card = select.closest('.js-rxs-card');
    const price = card.querySelector('.js-rxs-price');
    const compare = card.querySelector('.js-rxs-compare');
    if (price) price.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    card.querySelector('.js-rxs-add').dataset.variantId = option.value;
  }, true);

  // Adds the pick, then hands the response to the theme so the drawer (or the
  // cart page) re-renders the lines, the bar and the picks.
  document.addEventListener('click', async (evt) => {
    const button = evt.target.closest?.('.js-rxs-add');
    if (!button || button.disabled) return;
    evt.preventDefault();

    const card = button.closest('.js-rxs-card');
    const errorEl = card?.querySelector('.js-rxs-error');
    const variantId = Number(button.dataset.variantId);
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerSection = cartDrawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (errorEl) errorEl.hidden = true;

    try {
      const body = { items: [{ id: variantId, quantity: 1 }] };
      if (drawerSection) {
        body.sections = [drawerSection, 'cart-icon-bubble'];
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }

      announce(button.dataset.addedText);

      if (cartDrawer && data.sections) {
        await cartDrawer.renderContents(data, false);
        // The drawer is already open, so open() will not re-measure the
        // sticky summary for us.
        requestAnimationFrame(() => document.dispatchEvent(new CustomEvent('on:css-var-height:update')));
        focusLine(cartDrawer, variantId);
      } else {
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.refresh === 'function') {
          await cartItems.refresh();
        } else {
          window.location.reload();
          return;
        }
      }

      // Same event product-form.js fires, so the cart icon, the B2B recargo
      // sync and analytics listeners stay in step.
      const cartResponse = await fetch(`${theme.routes.cart}.js`);
      if (cartResponse.ok) {
        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { cart: await cartResponse.json(), variantId }
        }));
      }
    } catch (error) {
      // The card may already be gone if the cart re-rendered before failing.
      if (errorEl && errorEl.isConnected) {
        errorEl.textContent = error.message || theme.strings.cartError;
        errorEl.hidden = false;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();
